/**
 * Modale « Prendre rendez-vous ».
 *
 * Pilote l'élément <dialog> rendu par snippets/oyw-rdv-modal.liquid :
 *  - ouverture par tout élément [data-oyw-rdv-open] ;
 *  - bascule salle de sport / gestionnaire, qui change le libellé de la structure ;
 *  - calendrier défilant : les dates disponibles sont calculées à l'exécution
 *    et parcourues par vues successives, à l'aide des deux flèches ;
 *  - recopie du choix dans les champs cachés du {% form 'contact' %} ;
 *  - réouverture sur l'écran de confirmation après le rechargement de Shopify.
 *
 * <dialog>.showModal() fournit le piège à focus et la fermeture par Échap :
 * rien n'est réimplémenté ici.
 */
(() => {
  const modale = document.getElementById('oyw-rdv');
  if (!modale) return;

  const conteneurJours = modale.querySelector('[data-oyw-rdv-jours]');
  const boutonPrec = modale.querySelector('[data-oyw-rdv-prec]');
  const boutonSuiv = modale.querySelector('[data-oyw-rdv-suiv]');
  const periode = modale.querySelector('[data-oyw-rdv-periode]');
  const rappel = modale.querySelector('[data-oyw-rdv-rappel]');
  const boutonsCreneau = [...modale.querySelectorAll('[data-oyw-rdv-creneau]')];
  const onglets = [...modale.querySelectorAll('[data-oyw-rdv-type]')];
  const labelStructure = modale.querySelector('[data-oyw-rdv-label-structure]');
  const inputStructure = modale.querySelector('[data-oyw-rdv-input-structure]');
  const champType = modale.querySelector('[data-oyw-rdv-champ-type]');
  const champRdv = modale.querySelector('[data-oyw-rdv-champ-rdv]');
  const resume = modale.querySelector('[data-oyw-rdv-resume]');
  const blocFormulaire = modale.querySelector('[data-oyw-rdv-formulaire]');
  const blocConfirmation = modale.querySelector('[data-oyw-rdv-confirmation]');

  /** Libellés de la structure selon l'onglet actif. */
  const STRUCTURE = {
    salle: { label: 'Nom de la salle', exemple: 'Fit Club Paris 11', valeur: 'Salle de sport' },
    // La clé reste « exploitant » : elle est écrite dans les gabarits.
    exploitant: { label: 'Société / ville', exemple: 'Ma société, Versailles', valeur: 'Gestionnaire' },
  };

  const reglages = {
    parVue: Number(modale.dataset.nbJours) || 5,
    semaines: Number(modale.dataset.semaines) || 8,
    delai: Number(modale.dataset.delai) || 0,
    samedi: modale.dataset.samedi === 'true',
    dimanche: modale.dataset.dimanche === 'true',
  };

  /* ----------------------------------------------------- Les dates offertes */

  /** Un jour est proposable selon les cases « samedi » et « dimanche ». */
  const jourOuvert = (date) => {
    const j = date.getDay();
    if (j === 6) return reglages.samedi;
    if (j === 0) return reglages.dimanche;
    return true;
  };

  /**
   * Toutes les dates proposables, du délai minimum jusqu'à la profondeur
   * réglée. La liste est construite une fois à l'ouverture de la page ; elle
   * couvre plusieurs semaines, que les flèches parcourent par vues.
   */
  const construireDates = () => {
    const dates = [];
    const curseur = new Date();
    curseur.setHours(0, 0, 0, 0);
    curseur.setDate(curseur.getDate() + reglages.delai);

    const fin = new Date(curseur);
    fin.setDate(fin.getDate() + reglages.semaines * 7);

    while (curseur <= fin) {
      if (jourOuvert(curseur)) dates.push(new Date(curseur));
      curseur.setDate(curseur.getDate() + 1);
    }
    return dates;
  };

  const dates = construireDates();
  if (dates.length === 0) return;

  const nbVues = Math.ceil(dates.length / reglages.parVue);

  let vue = 0;
  let jourChoisi = 0; // index dans « dates »
  let creneauChoisi = 0;

  /* ------------------------------------------------------------- Rendu */

  const sansPoint = (texte) => texte.replace('.', '');

  const enLettres = (date) =>
    date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  /** Les dates de la vue courante. */
  const vueCourante = () =>
    dates.slice(vue * reglages.parVue, vue * reglages.parVue + reglages.parVue);

  /** « octobre 2026 », ou « octobre – novembre 2026 » si la vue est à cheval. */
  const libellePeriode = (visibles) => {
    if (visibles.length === 0) return '';
    const mois = (d) => d.toLocaleDateString('fr-FR', { month: 'long' });
    const premier = visibles[0];
    const dernier = visibles[visibles.length - 1];
    const annee = dernier.getFullYear();
    return mois(premier) === mois(dernier)
      ? `${mois(premier)} ${annee}`
      : `${mois(premier)} – ${mois(dernier)} ${annee}`;
  };

  /**
   * Construit les pastilles de la vue courante.
   *
   * Appelée au changement de vue seulement, jamais sur un simple changement de
   * sélection : reconstruire les boutons pendant le traitement d'un clic
   * détacherait du DOM le bouton cliqué, et l'événement finirait sa course avec
   * une cible orpheline — ce qui faisait fermer la modale.
   */
  const dessinerVue = () => {
    const visibles = vueCourante();
    conteneurJours.replaceChildren();
    conteneurJours.style.gridTemplateColumns = `repeat(${reglages.parVue}, 1fr)`;

    visibles.forEach((date, rang) => {
      const index = vue * reglages.parVue + rang;
      const bouton = document.createElement('button');
      bouton.type = 'button';
      bouton.className = 'oyw-rdv__jour';
      bouton.dataset.index = String(index);
      bouton.setAttribute('aria-label', enLettres(date));
      bouton.innerHTML = `
        <span class="oyw-rdv__jour-sem">${sansPoint(date.toLocaleDateString('fr-FR', { weekday: 'short' }))}</span>
        <span class="oyw-rdv__jour-num">${date.getDate()}</span>
        <span class="oyw-rdv__jour-mois">${sansPoint(date.toLocaleDateString('fr-FR', { month: 'short' }))}</span>
      `;
      bouton.addEventListener('click', () => {
        jourChoisi = index;
        majSelection();
      });
      conteneurJours.append(bouton);
    });

    if (periode) periode.textContent = libellePeriode(visibles);
    if (boutonPrec) boutonPrec.disabled = vue === 0;
    if (boutonSuiv) boutonSuiv.disabled = vue >= nbVues - 1;
  };

  const allerA = (nouvelleVue) => {
    vue = Math.min(Math.max(nouvelleVue, 0), nbVues - 1);
    dessinerVue();
    majSelection();
  };

  boutonPrec?.addEventListener('click', () => allerA(vue - 1));
  boutonSuiv?.addEventListener('click', () => allerA(vue + 1));

  dessinerVue();

  /* ------------------------------------------------- Synchronisation de l'état */

  /** Le rendez-vous en clair, tel qu'il partira dans l'e-mail. */
  const libelleRendezVous = () => {
    const date = dates[jourChoisi];
    if (!date) return '';
    const creneau = boutonsCreneau[creneauChoisi]?.textContent.trim() ?? '';
    return `${enLettres(date)} à ${creneau}`;
  };

  const majSelection = () => {
    // Bascule d'attributs uniquement : aucun nœud n'est retiré du DOM.
    conteneurJours.querySelectorAll('.oyw-rdv__jour').forEach((bouton) => {
      bouton.setAttribute('aria-pressed', String(Number(bouton.dataset.index) === jourChoisi));
    });
    boutonsCreneau.forEach((bouton, index) => {
      bouton.setAttribute('aria-pressed', String(index === creneauChoisi));
    });

    // Le jour retenu peut être hors de la vue affichée : on le rappelle en clair.
    if (rappel) {
      const dansLaVue = jourChoisi >= vue * reglages.parVue
        && jourChoisi < vue * reglages.parVue + reglages.parVue;
      rappel.hidden = dansLaVue;
      if (!dansLaVue) rappel.textContent = `Jour retenu : ${enLettres(dates[jourChoisi])}`;
    }

    const libelle = libelleRendezVous();
    if (champRdv) champRdv.value = libelle;
    if (resume) resume.textContent = libelle ? `Confirmer le ${libelle}` : 'Confirmer le rendez-vous';
  };

  boutonsCreneau.forEach((bouton, index) => {
    bouton.addEventListener('click', () => {
      creneauChoisi = index;
      majSelection();
    });
  });

  /* ------------------------------------------------------------- Les onglets */

  const appliquerType = (type) => {
    const config = STRUCTURE[type] ?? STRUCTURE.salle;
    onglets.forEach((onglet) => {
      onglet.setAttribute('aria-selected', String(onglet.dataset.oywRdvType === type));
    });
    if (labelStructure) labelStructure.textContent = config.label;
    if (inputStructure) inputStructure.placeholder = config.exemple;
    if (champType) champType.value = config.valeur;
  };

  onglets.forEach((onglet) => {
    onglet.addEventListener('click', () => appliquerType(onglet.dataset.oywRdvType));
  });

  /* ------------------------------------------------- Ouverture et fermeture */

  const afficher = (ecran) => {
    const confirmation = ecran === 'confirmation';
    if (blocConfirmation) blocConfirmation.hidden = !confirmation;
    if (blocFormulaire) blocFormulaire.hidden = confirmation;
  };

  const ouvrir = (type, ecran = 'formulaire') => {
    afficher(ecran);
    if (ecran === 'formulaire') {
      appliquerType(type || 'salle');
      majSelection();
    }
    if (!modale.open) modale.showModal();
  };

  const fermer = () => {
    if (modale.open) modale.close();
  };

  document.addEventListener('click', (evenement) => {
    const declencheur = evenement.target.closest('[data-oyw-rdv-open]');
    if (declencheur) {
      evenement.preventDefault();
      ouvrir(declencheur.dataset.oywRdvType);
      return;
    }
    if (evenement.target.closest('[data-oyw-rdv-close]')) fermer();
  });

  // Un clic dans le vide autour du panneau ferme la modale. On teste l'égalité
  // avec le <dialog> lui-même plutôt que l'absence d'ancêtre .oyw-rdv__panneau :
  // un clic sur le fond a bien le dialog pour cible, et ce test reste juste même
  // si le bouton cliqué a été retiré du DOM par son propre gestionnaire.
  modale.addEventListener('click', (evenement) => {
    if (evenement.target === modale) fermer();
  });

  /* ------------------------------------- Retour après soumission du formulaire */

  // Shopify recharge la page après l'envoi : succès signalé dans l'URL,
  // erreurs rendues dans le formulaire par Liquid.
  const parametres = new URLSearchParams(window.location.search);
  if (parametres.get('contact_posted') === 'true') {
    ouvrir(null, 'confirmation');
  } else if (modale.querySelector('[data-oyw-rdv-erreur]')) {
    ouvrir('salle');
  } else {
    afficher('formulaire');
    majSelection();
  }
})();
